import { makeStyles } from '@mui/styles'
import { useBooleanState } from '../../hooks/useBooleanState'
import { MenuItem, SelectChangeEvent } from '@mui/material'
import { SelectInputOption } from '../SelectInput/SelectInput.types'
import Typography from '@mui/material/Typography'
import React, { useEffect } from 'react'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import FormControl from '@mui/material/FormControl'
import SvgIcon from '@mui/material/SvgIcon'
import { useHistory, useLocation } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { ReactComponent as ArrowDownIcon } from '../../../assets/images/arrows/down-bold.svg'
import { useGetRoadmapsQuery } from '../../queries/useGetRoadmapsQuery'
import { useMyStrategyTierQuery } from '../../queries/useMyStrategyTierQuery'
import { setStrategy } from '../../../redux/actions'
import { initialState } from '../../../redux/reducers'
import { useDispatch, useSelector } from 'react-redux'

const useStyles = makeStyles((theme) => ({
    paper: {
        borderRadius: '16px !important',
        backgroundColor: `${theme.palette.figma.grayscale.tier7} !important`,
        boxShadow: '0px 8px 16px 0px rgba(0, 0, 0, 0.17); !important',
        transition: 'none !important',
        maxHeight: '300px !important',
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: `${theme.spacing(1.5)} !important`,
        padding: `${theme.spacing(2)} !important`,
        '& > li': {
            minHeight: 'auto !important',
        },
    },
}))

const SelectInputIcon = ({ className }: { className: string }) => (
    <SvgIcon
        className={className}
        component={ArrowDownIcon}
        inheritViewBox
        color="inherit"
        sx={{ fontSize: '18px', color: 'inherit !important' }}
    />
)

const getRoadmapLabel = (roadmap?: { product?: string; indication?: string }) =>
    roadmap?.product ? `${roadmap.product} for ${roadmap.indication}` : ''

const RoadmapSelector = () => {
    const styles = useStyles()
    const open = useBooleanState()

    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation()
    const queryClient = useQueryClient()

    // @ts-expect-error store is not typed properly
    const strategyId = useSelector((state) => state.strategy?._id)
    // The whole store is rehydrated from localStorage before any request goes out, so the current
    // roadmap's name is on hand immediately. Without it the picker renders an empty pill until the
    // roadmaps arrive. Selected as a string rather than the object, so an edit elsewhere in the
    // roadmap does not re-render the picker.
    // @ts-expect-error store is not typed properly
    const storedRoadmapLabel = useSelector((state) => getRoadmapLabel(state.strategy))

    const { data, isLoading } = useGetRoadmapsQuery({
        onGetRoadmaps: (roadmaps) => {
            if (!strategyId && !!roadmaps?.length) {
                dispatch(setStrategy(roadmaps[0]))
            }
        },
    })

    // myTier resolves to null once the backend can no longer find a StrategyPermissions row for
    // this roadmap -- e.g. a roadmap admin revoked this user's own access while they were still
    // on it. No polling -- instead this re-checks on every page navigation (a hard browser
    // refresh already gets a fresh fetch for free, with no cache to go stale). If it comes back
    // null, deselect the now-unreachable roadmap and send them back to roadmap selection, same
    // as if they'd never picked one. Also drop the cached roadmaps list so the picker on that
    // screen doesn't keep offering a roadmap they can no longer reach.
    const { data: myTier, refetch: refetchMyTier } = useMyStrategyTierQuery(strategyId)
    useEffect(() => {
        if (strategyId) {
            refetchMyTier()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, strategyId])

    useEffect(() => {
        if (strategyId && myTier === null) {
            dispatch(setStrategy(initialState.strategy))
            queryClient.invalidateQueries(['roadmaps'])
            history.push('/roadmaps')
        }
    }, [strategyId, myTier, dispatch, queryClient, history])

    const options = (data || [])
        .filter((roadmap) => roadmap.isActive !== false)
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
        .map((roadmap) => ({
            id: roadmap._id,
            label: getRoadmapLabel(roadmap),
        }))

    const selected = options.find((option) => option.id === strategyId)?.id

    const handleChange = (event: SelectChangeEvent<unknown>) => {
        const value = event.target.value as string

        const roadmap = data.find((roadmap) => roadmap._id === value)
        dispatch(setStrategy(roadmap))
    }

    const getOptionById = (options: SelectInputOption[], id: string) => options.find((option) => option.id === id)

    const label = getOptionById(options, selected)?.label || storedRoadmapLabel

    const renderValue = () => {
        if (!label) {
            return null
        }

        return (
            <Typography
                variant="16x600"
                color="figma.primary"
                px={1.5}
                py={1}
                borderRadius="8px"
                bgcolor="figma.primary3"
                lineHeight={1}
                maxWidth={{ xs: '225px', md: '300px' }}
                overflow="hidden"
                textOverflow="ellipsis"
            >
                {label}
            </Typography>
        )
    }

    return (
        <FormControl fullWidth>
            <Select
                // Only while there is nothing to show: a disabled input greys its own text, so
                // disabling it with a name in it is the grey flash on every page load.
                disabled={isLoading && !label}
                MenuProps={{
                    classes: {
                        paper: styles.paper,
                        list: styles.list,
                    },
                    anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                    },
                    transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                    },
                }}
                open={open.state}
                onClose={open.handleSetAsFalse}
                onOpen={open.handleSetAsTrue}
                variant="outlined"
                value={selected ?? ''}
                onChange={handleChange}
                displayEmpty
                renderValue={renderValue}
                IconComponent={SelectInputIcon}
                sx={(theme) => ({
                    borderRadius: '16px',
                    '& .MuiOutlinedInput-notchedOutline': {
                        border: `unset !important`,
                    },
                    '& .MuiOutlinedInput-input': {
                        display: 'flex',
                        p: 0.5,
                        pr: `${theme.spacing(4)} !important`,
                    },
                    '& .MuiSelect-icon': {
                        color: `${theme.palette.figma.primary} !important`,
                    },
                })}
            >
                {options.map((option) => {
                    const isSelected = selected === option.id

                    return (
                        <MenuItem
                            key={option.id}
                            selected={isSelected}
                            value={option.id}
                            disableRipple
                            sx={(theme) => ({
                                flex: 1,
                                p: 0,
                                borderRadius: '8px',
                                color: isSelected ? 'figma.primary' : 'figma.grayscale.textHier2',
                                backgroundColor: isSelected
                                    ? `${theme.palette.figma.primary3} !important`
                                    : 'unset !important',
                                mx: isSelected ? -1 : 0,
                                my: isSelected ? -0.5 : 0,
                                px: isSelected ? 1 : 0,
                                py: isSelected ? 0.5 : 0,
                                '&:hover': {
                                    color: 'figma.primary',
                                },
                            })}
                        >
                            <Stack direction="row" gap={1}>
                                <Typography variant="16x500" color="inherit" sx={{ mt: -0.25 }}>
                                    {option.label}
                                </Typography>
                            </Stack>
                        </MenuItem>
                    )
                })}
            </Select>
        </FormControl>
    )
}

export default RoadmapSelector
